import React, { useEffect, useMemo, useState } from 'react';
import {
  Zap,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  Power,
  AlertCircle,
} from 'lucide-react';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';
import { CuteConfirmModal } from '@/shared/components/CuteConfirmModal';
import {
  getAutomations,
  createAutomation,
  updateAutomation,
  getTemplates,
  EmailAutomation,
  EmailTemplate,
  CreateAutomationDto,
  getTriggerLabel,
  ACTIVE_TRIGGERS,
  COMING_SOON_TRIGGERS,
} from '@/services/emailCrmService';

const ALL_TRIGGERS = [...ACTIVE_TRIGGERS, ...COMING_SOON_TRIGGERS] as string[];

const triggerHelp: Record<string, string> = {
  LEAD_ADDED: 'Se déclenche dès qu\'un nouveau lead est ajouté (manuellement ou par import).',
  FORMER_USER_ADDED: 'Se déclenche quand un ancien utilisateur de la plateforme devient un lead.',
  LEAD_INACTIVE: 'Se déclenche pour les leads qui n\'ont pas été contactés depuis N jours.',
  PAYMENT_REMINDER: 'Se déclenche avant l\'échéance d\'un paiement.',
  TRIAL_ENDING: 'Se déclenche avant la fin de la période d\'essai.',
  WELCOME: 'Email de bienvenue à l\'inscription d\'un utilisateur.',
  ABANDONED_SIGNUP: 'Se déclenche quand une inscription a été commencée mais non terminée.',
  RE_ENGAGEMENT: 'Email de réengagement pour les utilisateurs inactifs.',
  DISCOUNT_OFFER: 'Email avec une offre promotionnelle.',
  SUBSCRIPTION_RENEWED: 'Confirmation après renouvellement d\'abonnement.',
};

interface FormState extends CreateAutomationDto {}

const emptyForm: FormState = {
  name: '',
  description: '',
  triggerType: 'LEAD_ADDED',
  triggerDelay: 0,
  triggerConfig: {},
  templateId: undefined,
  subject: '',
  content: '',
};

export const EmailAutomationsPage: React.FC = () => {
  const { toasts, removeToast, success, error } = useToast();

  const [automations, setAutomations] = useState<EmailAutomation[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [a, t] = await Promise.all([getAutomations(), getTemplates()]);
      setAutomations(a);
      setTemplates(t);
    } catch (err: any) {
      console.error(err);
      error('Erreur', err?.message ?? 'Impossible de charger les automatisations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEdit = (a: EmailAutomation) => {
    setEditingId(a.id);
    setFormData({
      name: a.name,
      description: a.description ?? '',
      triggerType: a.triggerType,
      triggerDelay: a.triggerDelay,
      triggerConfig: (a.triggerConfig ?? {}) as Record<string, unknown>,
      templateId: a.templateId ?? undefined,
      subject: a.subject ?? '',
      content: a.content ?? '',
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return error('Nom requis', '');
    if (!formData.templateId && !(formData.subject && formData.content)) {
      return error('Template requis ou objet/contenu manuel', '');
    }
    setSaving(true);
    try {
      const payload: CreateAutomationDto = {
        ...formData,
        // Template-wins: clear inline subject/content when a template is selected
        subject: formData.templateId ? undefined : formData.subject,
        content: formData.templateId ? undefined : formData.content,
      };
      if (editingId) {
        await updateAutomation(editingId, payload);
        success('Automatisation mise à jour', '');
      } else {
        await createAutomation(payload);
        success('Automatisation créée', '');
      }
      setShowForm(false);
      fetchAll();
    } catch (err: any) {
      error('Erreur', err?.message ?? 'Échec');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (a: EmailAutomation) => {
    try {
      await updateAutomation(a.id, { isActive: !a.isActive });
      fetchAll();
    } catch (err: any) {
      error('Erreur', err?.message ?? 'Échec');
    }
  };

  const [disableTarget, setDisableTarget] = useState<EmailAutomation | null>(null);

  const askDisable = (a: EmailAutomation) => setDisableTarget(a);

  const confirmDisable = async () => {
    if (!disableTarget) return;
    try {
      await updateAutomation(disableTarget.id, { isActive: false });
      success('Désactivée', '');
      fetchAll();
    } catch (err: any) {
      error('Erreur', err?.message ?? 'Échec');
    } finally {
      setDisableTarget(null);
    }
  };

  const isComingSoon = (t: string) => COMING_SOON_TRIGGERS.includes(t);

  // For LEAD_INACTIVE — config field "daysInactive"
  const updateConfig = (k: string, v: unknown) =>
    setFormData((f) => ({ ...f, triggerConfig: { ...(f.triggerConfig ?? {}), [k]: v } }));

  const triggerConfig = useMemo(
    () => (formData.triggerConfig ?? {}) as Record<string, unknown>,
    [formData.triggerConfig],
  );

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Automatisations</h1>
            <p className="text-sm text-gray-500 mt-1">
              Emails déclenchés automatiquement par un événement (ajout d'un lead, ancien utilisateur, etc.).
            </p>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            <Plus size={16} />
            Nouvelle automatisation
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-indigo-600" />
          </div>
        ) : automations.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
            <Zap className="mx-auto mb-3 text-gray-300" size={48} />
            <p className="text-gray-500">Aucune automatisation.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {automations.map((a) => (
              <div
                key={a.id}
                className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900">{a.name}</h3>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded bg-indigo-50 text-indigo-700">
                      {getTriggerLabel(a.triggerType)}
                    </span>
                  </div>
                  <button
                    onClick={() => handleToggle(a)}
                    className={`p-2 rounded ${
                      a.isActive
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-50'
                    }`}
                    title={a.isActive ? 'Désactiver' : 'Activer'}
                  >
                    <Power size={16} />
                  </button>
                </div>
                {a.description && (
                  <p className="text-xs text-gray-500 mb-2">{a.description}</p>
                )}
                {a.triggerDelay > 0 && (
                  <p className="text-xs text-gray-500 mb-1">
                    Délai : {a.triggerDelay}h après l'événement
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  {a.totalTriggered ?? 0} déclenchement(s)
                </p>
                <div className="flex justify-end gap-1 pt-3 border-t border-gray-100 mt-3">
                  <button
                    onClick={() => openEdit(a)}
                    className="p-2 text-indigo-600 hover:bg-indigo-50 rounded"
                    title="Modifier"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => askDisable(a)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Désactiver"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Modifier l\'automatisation' : 'Nouvelle automatisation'}
              </h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Bienvenue nouveau lead"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  value={formData.description ?? ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Déclencheur *
                </label>
                <select
                  value={formData.triggerType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      triggerType: e.target.value as CreateAutomationDto['triggerType'],
                      triggerConfig: {},
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  {ALL_TRIGGERS.map((t) => (
                    <option key={t} value={t} disabled={isComingSoon(t)}>
                      {getTriggerLabel(t)}
                      {isComingSoon(t) ? ' — bientôt disponible' : ''}
                    </option>
                  ))}
                </select>
                {triggerHelp[formData.triggerType] && (
                  <p className="text-xs text-gray-500 mt-1">{triggerHelp[formData.triggerType]}</p>
                )}
                {isComingSoon(formData.triggerType) && (
                  <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                    <AlertCircle size={12} />
                    Ce déclencheur n'est pas encore connecté à un événement.
                  </p>
                )}
              </div>

              {formData.triggerType === 'LEAD_INACTIVE' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Inactivité (jours)
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={String(triggerConfig.daysInactive ?? 30)}
                    onChange={(e) => updateConfig('daysInactive', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Délai après l'événement (heures)
                </label>
                <input
                  type="number"
                  min={0}
                  value={formData.triggerDelay}
                  onChange={(e) =>
                    setFormData({ ...formData, triggerDelay: Number(e.target.value) })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  0 = envoi immédiat. Au-delà, les emails sont retardés en mémoire (le serveur doit rester actif).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template
                </label>
                <select
                  value={formData.templateId ?? ''}
                  onChange={(e) =>
                    setFormData({ ...formData, templateId: e.target.value || undefined })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">— Pas de template (saisie manuelle) —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {formData.templateId && (
                  <p className="text-xs text-gray-500 mt-1">
                    Le template fournit l'objet et le contenu de l'email.
                  </p>
                )}
              </div>

              {/* Inline content only when no template selected */}
              {!formData.templateId && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Objet de l'email *
                    </label>
                    <input
                      value={formData.subject ?? ''}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contenu HTML *
                    </label>
                    <textarea
                      value={formData.content ?? ''}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-xs"
                      placeholder="<html>...</html>"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || isComingSoon(formData.triggerType)}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                {editingId ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CuteConfirmModal
        isOpen={!!disableTarget}
        title="Désactiver cette automatisation"
        message="Elle ne se déclenchera plus jusqu'à ce que vous la réactiviez."
        itemName={disableTarget?.name}
        confirmLabel="Désactiver"
        variant="warning"
        onConfirm={confirmDisable}
        onCancel={() => setDisableTarget(null)}
      />
    </>
  );
};

export default EmailAutomationsPage;
